import type { FormikTouched } from 'formik';
import { isRecord } from '../../../utils/coerce';

/** Formik-style nested error object (string leaves). */
export type FormikErrorTree =
  | string
  | FormikErrorTree[]
  | {
      [field: string]: FormikErrorTree | undefined;
    };

/** Mirror of {@link FormikErrorTree} with `true` at former error leaves. */
export type FormikTouchedTree =
  | boolean
  | FormikTouchedTree[]
  | {
      [field: string]: FormikTouchedTree;
    };

export interface ErrorToTouchedAndIdsResult {
  touchedObj: FormikTouchedTree;
  errorIds: string[];
}

type ParentNode = Record<string | number, unknown> | unknown[];

interface WalkFrame {
  obj: unknown;
  parent: ParentNode;
  key: string | number;
  path: string;
}

function setChild(
  parent: ParentNode,
  key: string | number,
  value: unknown
): void {
  if (Array.isArray(parent)) {
    const index = typeof key === 'number' ? key : Number(key);
    parent[index] = value;
    return;
  }
  parent[key] = value;
}

function getChildContainer(
  parent: ParentNode,
  key: string | number
): ParentNode | undefined {
  const value = Array.isArray(parent)
    ? parent[typeof key === 'number' ? key : Number(key)]
    : parent[key];
  if (Array.isArray(value)) {
    return value;
  }
  if (isRecord(value)) {
    return value;
  }
  return undefined;
}

function isFormikErrorRecord(
  value: unknown
): value is { [field: string]: FormikErrorTree | undefined } {
  return isRecord(value);
}

/**
 * Generate field paths and a touched mirror from a Formik-style errors object.
 */
export function errorToTouchedAndIds(
  obj: FormikErrorTree
): ErrorToTouchedAndIdsResult {
  const result: Record<string, FormikTouchedTree> = {};
  const errorIds: string[] = [];
  let objectsTodo: WalkFrame[] = [
    {
      obj,
      parent: result,
      key: 'root',
      path: '',
    },
  ];

  while (objectsTodo.length) {
    const frame = objectsTodo.pop();
    if (!frame) {
      break;
    }
    const { obj: currentObj, parent, key, path } = frame;

    if (Array.isArray(currentObj)) {
      setChild(parent, key, []);
      const newParent = getChildContainer(parent, key);
      if (!newParent || !Array.isArray(newParent)) {
        continue;
      }
      const newTodos = currentObj.map((value, index) => ({
        obj: value,
        parent: newParent,
        key: index,
        path: `${path}[${index}]`,
      }));
      objectsTodo = objectsTodo.concat(newTodos);
    } else if (isFormikErrorRecord(currentObj)) {
      setChild(parent, key, {});
      const newParent = getChildContainer(parent, key);
      if (!newParent || Array.isArray(newParent)) {
        continue;
      }
      const record = currentObj;
      const newTodos = Object.keys(record).map((fieldKey) => ({
        obj: record[fieldKey],
        parent: newParent,
        key: fieldKey,
        path: `${path}.${fieldKey}`,
      }));
      objectsTodo = objectsTodo.concat(newTodos);
    } else if (currentObj !== undefined && currentObj !== null) {
      setChild(parent, key, true);
      errorIds.push(path.slice(1));
    }
  }

  return {
    touchedObj: result.root ?? {},
    errorIds,
  };
}

/** Bridge nested touched tree to Formik's touched shape. */
export function toFormikTouched<T>(tree: FormikTouchedTree): FormikTouched<T> {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- mirrors Formik touched structure
  return tree as unknown as FormikTouched<T>;
}
